/**
 * Gift box + gift note: opens gift drawer, syncs line item property + cart order attribute via cart-sections (WithShopifyCart).
 */
const GIFT_MODAL_ID = 'gift-note-dialog';
const MAX_CHARS = 500;
const CHARS_REMAINING_TOKEN = '__COUNT__';

function readConfig() {
  const el = document.getElementById('gift-box-config');
  if (!el) {
    return { variantId: '39485319250020', giftNoteKey: 'Gift Note', maxChars: MAX_CHARS };
  }
  try {
    const parsed = JSON.parse(el.textContent);
    return {
      variantId: String(parsed.variantId || '39485319250020'),
      giftNoteKey: parsed.giftNoteKey || 'Gift Note',
      maxChars: Number(parsed.maxChars) || MAX_CHARS,
    };
  } catch {
    return { variantId: '39485319250020', giftNoteKey: 'Gift Note', maxChars: MAX_CHARS };
  }
}

function appProvider() {
  return document.querySelector('app-provider');
}

function cartDrawerSections() {
  return document.querySelector('cart-sections[section-id="cart-drawer"]');
}

function dispatchModal(type, id) {
  const ap = appProvider();
  if (!ap) return;
  ap.dispatchEvent(
    new CustomEvent(type, {
      detail: id,
      bubbles: true,
      composed: true,
    }),
  );
}

/**
 * modal-dialog (Lit) always renders a shadow header row with the default X.
 * header-classes cannot win over the component's baked-in `flex`, and theme
 * utilities on parent-class do not pierce shadow for `.drawer` background.
 */
function applyGiftNoteDrawerChrome() {
  const root = document.getElementById(GIFT_MODAL_ID);
  if (!root?.shadowRoot) return;
  const shell = root.shadowRoot.querySelector('div.drawer.grid');
  if (!shell) return;
  const header = shell.querySelector(':scope > div:first-of-type');
  if (header) {
    header.style.setProperty('display', 'none', 'important');
  }
  shell.style.setProperty('background-color', '#ffffff', 'important');
}

function wireGiftNoteModalChrome(root) {
  if (!root || root.dataset.giftChromeWired === 'true') return;
  root.dataset.giftChromeWired = 'true';
  applyGiftNoteDrawerChrome();
  const obs = new MutationObserver(() => {
    if (root.hasAttribute('open')) applyGiftNoteDrawerChrome();
  });
  obs.observe(root, { attributes: true, attributeFilter: ['open'] });
}

function openGiftDrawer() {
  dispatchModal('modal-dialog::open', GIFT_MODAL_ID);
  requestAnimationFrame(() => {
    applyGiftNoteDrawerChrome();
    setTimeout(applyGiftNoteDrawerChrome, 50);
    setTimeout(applyGiftNoteDrawerChrome, 200);
  });
}

function closeGiftDrawer() {
  dispatchModal('modal-dialog::close', GIFT_MODAL_ID);
}

function strLabelCount(value) {
  return [...String(value)].length;
}

function normalizeNote(raw) {
  return String(raw ?? '').trim();
}

function getDrawerEls() {
  const root = document.getElementById(GIFT_MODAL_ID);
  if (!root) return null;
  return {
    root,
    textarea: root.querySelector('[data-gift-note-input]'),
    counter: root.querySelector('[data-gift-note-counter]'),
    err: root.querySelector('[data-gift-note-error]'),
    submit: root.querySelector('[data-gift-note-submit]'),
  };
}

function giftStrings() {
  return window.__giftBoxStrings || {};
}

function initGiftBoxCart() {
  const els = getDrawerEls();
  if (!els || !els.textarea || !els.counter || !els.err || !els.submit) return;

  wireGiftNoteModalChrome(els.root);
  customElements.whenDefined('modal-dialog').then(() => applyGiftNoteDrawerChrome());

  const { textarea, counter, err, submit } = els;
  const cfg = readConfig();
  const max = cfg.maxChars || MAX_CHARS;
  const noteKey = cfg.giftNoteKey;
  const variantNum = Number(cfg.variantId);

  let mode = 'add';
  let lineKey = '';

  function syncCounter() {
    const len = strLabelCount(textarea.value);
    const remaining = Math.max(0, max - len);
    const tpl = giftStrings().charsRemainingTemplate;
    counter.textContent =
      typeof tpl === 'string' && tpl.includes(CHARS_REMAINING_TOKEN)
        ? tpl.split(CHARS_REMAINING_TOKEN).join(String(remaining))
        : `${remaining} / ${max}`;
    const over = len > max;
    err.textContent = over ? giftStrings().noteTooLong || '' : '';
    err.classList.toggle('hidden', !over);
    const cs = cartDrawerSections();
    const loading = cs && cs.shopifyCartLoading;
    submit.disabled = over || !!loading;
  }

  textarea.addEventListener('input', syncCounter);
  syncCounter();

  function prepFromTrigger(trigger) {
    mode = trigger.dataset.giftMode === 'edit' ? 'edit' : 'add';
    lineKey = trigger.dataset.lineKey || '';
    const rawAttr = trigger.getAttribute('data-initial-note');
    if (rawAttr != null) {
      try {
        textarea.value = JSON.parse(rawAttr);
      } catch {
        textarea.value = rawAttr;
      }
    } else {
      textarea.value = '';
    }
    submit.textContent =
      mode === 'edit'
        ? giftStrings().updateNote || 'Update note'
        : giftStrings().addToOrder || 'Add to order';
    syncCounter();
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-open-gift-note]');
    if (!openBtn) return;
    e.preventDefault();
    prepFromTrigger(openBtn);
    openGiftDrawer();
  });

  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-close-gift-note]');
    if (closeBtn) {
      e.preventDefault();
      closeGiftDrawer();
    }
  });

  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-gift-remove]');
    if (!rm) return;
    e.preventDefault();
    const key = rm.dataset.lineKey;
    const cs = cartDrawerSections();
    if (!cs || !key || typeof cs.removeItemFromCart !== 'function') return;
    try {
      await cs.removeItemFromCart([key]);
      if (typeof cs.updateCartNoteAndAttributes === 'function') {
        await cs.updateCartNoteAndAttributes({
          attributes: { [noteKey]: '' },
        });
      }
    } catch (err) {
      console.error('[gift-box-cart] remove failed', err);
    }
  });

  submit.addEventListener('click', async () => {
    const cs = cartDrawerSections();
    if (!cs) {
      console.warn('[gift-box-cart] cart-sections not found');
      return;
    }
    const note = normalizeNote(textarea.value);
    const props = { [noteKey]: note };
    if (strLabelCount(textarea.value) > max) return;

    submit.disabled = true;
    try {
      if (mode === 'add') {
        if (typeof cs.addItemToCart !== 'function' || typeof cs.updateCartNoteAndAttributes !== 'function') return;
        await cs.addItemToCart([
          {
            id: variantNum,
            quantity: 1,
            properties: props,
          },
        ]);
        await cs.updateCartNoteAndAttributes({ attributes: props });
      } else if (mode === 'edit' && lineKey) {
        if (typeof cs.changeItemToCart !== 'function' || typeof cs.updateCartNoteAndAttributes !== 'function') return;
        await cs.changeItemToCart({
          id: lineKey,
          quantity: 1,
          properties: props,
        });
        await cs.updateCartNoteAndAttributes({ attributes: props });
      }
      closeGiftDrawer();
    } catch (err) {
      console.error('[gift-box-cart] submit failed', err);
    } finally {
      syncCounter();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftBoxCart);
} else {
  initGiftBoxCart();
}
